import { LOYALTY_PROGRAMM, type LoyaltyKonto, type LoyaltyKontenListe, type LoyaltyRegelwerk, type LoyaltyStatus, type Me } from '@portal/contracts';
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { HttpError, loyaltyApi } from '../../api.js';
import { navigate, onLinkClick } from '../../router.js';
import { Problem } from '../ModulePage.js';
import {
  akteurName, aktiverReiter, artAnzeige, bonusAnzeige, crownsAnzeige, darfVerwalten, haeufigkeit, kontoPfad, loyaltyRoute, schwelleAnzeige, stufeAnzeige,
  UNTERNAVIGATION, vorteileJeStufe, wertAnzeige, type LoyaltyRoute,
} from './loyalty-ui.js';
import './loyalty.css';

type Laden<T> = { kind: 'laden' } | { kind: 'fehler'; status: number } | { kind: 'ok'; daten: T };
const DATUM = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: 'Europe/Berlin' });
const datum = (s: string | null) => (s ? DATUM.format(new Date(s.length === 10 ? `${s}T12:00:00` : s)) : '—');

/** Daten laden; nur die jüngste Antwort zählt */
function useLaden<T>(fn: () => Promise<T>, deps: unknown[]): [Laden<T>, (d: T) => void] {
  const [state, setState] = useState<Laden<T>>({ kind: 'laden' });
  useEffect(() => {
    let alive = true;
    setState({ kind: 'laden' });
    fn().then(
      (daten) => alive && setState({ kind: 'ok', daten }),
      (err: unknown) => alive && setState({ kind: 'fehler', status: err instanceof HttpError ? err.status : 0 }),
    );
    return () => {
      alive = false;
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [state, (daten: T) => setState({ kind: 'ok', daten })];
}

/**
 * „House of Brückner - Royalty“ — Team-Sicht (#854 Phase 1, #856 Phase 2a): Unternavigation Übersicht | Konten | Regelwerk,
 * Kontodetail mit Stufenhistorie und Royal-Aktion (nur Stufe verwalten). Rechte prüft die API (Modul-Gate + Cerbos);
 * die Oberfläche blendet nur aus. Keine Buchungen (Crowns/Umsatz folgen in Phase 2b).
 */
export function Loyalty({ me, pathname }: { me: Me; pathname: string }) {
  const route = loyaltyRoute(pathname);
  if (route.seite === 'unbekannt') return <Problem status={404} anrede="sie" />;
  return (
    <section className="mod loyalty">
      <h1 className="ap-title">{LOYALTY_PROGRAMM}</h1>
      <Reiter route={route} />
      {route.seite === 'uebersicht' && <Uebersicht />}
      {route.seite === 'konten' && <Konten />}
      {route.seite === 'konto' && <Konto key={route.id} id={route.id} verwalten={darfVerwalten(me)} />}
      {route.seite === 'regelwerk' && <Regelwerk />}
    </section>
  );
}

function Reiter({ route }: { route: LoyaltyRoute }) {
  const aktiv = aktiverReiter(route);
  return (
    <nav className="loyalty-reiter" aria-label={`${LOYALTY_PROGRAMM} – Bereiche`}>
      {UNTERNAVIGATION.map((u) => (
        <a key={u.seite} href={u.pfad} className="loyalty-reiter__link" aria-current={aktiv === u.seite ? 'page' : undefined} onClick={onLinkClick(u.pfad)}>
          {u.label}
        </a>
      ))}
    </nav>
  );
}

function Status<T>({ state }: { state: Laden<T> }) {
  if (state.kind === 'laden') return <p className="sh-status" role="status">Wird geladen …</p>;
  if (state.kind === 'fehler') return <Problem status={state.status} anrede="sie" />;
  return null;
}

function Uebersicht() {
  const [state] = useLaden<LoyaltyStatus>(() => loyaltyApi.status(), []);
  if (state.kind !== 'ok') return <Status state={state} />;
  return (
    <div className="loyalty-kacheln">
      <div className="pb-card loyalty-kachel">
        <p className="loyalty-kachel__label">Konten</p>
        <p className="loyalty-kachel__wert">{new Intl.NumberFormat('de-DE').format(state.daten.konten)}</p>
      </div>
      <div className="pb-card loyalty-kachel">
        <p className="loyalty-kachel__label">Regelwerk</p>
        <p className="loyalty-kachel__wert">{state.daten.regelwerk === null ? '—' : `Version ${state.daten.regelwerk}`}</p>
      </div>
      <div className="pb-card loyalty-kachel loyalty-kachel--breit">
        <p className="loyalty-kachel__label">Ausbaustufe</p>
        <p className="loyalty-hinweis">Regelwerk und Konten. Crowns, Umsätze und Vorteile werden noch nicht gebucht; der Abgleich mit Shop und Filiale folgt.</p>
      </div>
    </div>
  );
}

function Konten() {
  const [suche, setSuche] = useState('');
  const [aktiv, setAktiv] = useState('');
  const [state] = useLaden<LoyaltyKontenListe>(() => loyaltyApi.konten(1, 50, aktiv), [aktiv]);
  const sucheId = useId();
  const absenden = (e: FormEvent) => {
    e.preventDefault();
    setAktiv(suche.trim());
  };
  return (
    <>
      <form className="loyalty-suche" role="search" onSubmit={absenden}>
        <label htmlFor={sucheId} className="loyalty-suche__label">Konto suchen</label>
        <input id={sucheId} className="pb-input loyalty-suche__feld" type="search" value={suche} maxLength={100} placeholder="Name oder E-Mail" onChange={(e) => setSuche(e.target.value)} />
        <button type="submit" className="pb-button">Suchen</button>
      </form>
      {state.kind !== 'ok' ? (
        <Status state={state} />
      ) : (
        <div className="pb-card loyalty-tabelle-wrap">
          <table className="loyalty-tabelle">
            <caption className="sr-only">Konten mit Name, Stufe, Stufe seit und Royal-Kennzeichen</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Stufe</th>
                <th scope="col">seit</th>
                <th scope="col">Royal</th>
              </tr>
            </thead>
            <tbody>
              {state.daten.eintraege.length === 0 ? (
                <tr className="loyalty-leer">
                  <td colSpan={4}>{aktiv ? 'Keine Konten für diese Suche.' : 'Noch keine Konten.'}</td>
                </tr>
              ) : (
                state.daten.eintraege.map((k) => (
                  <tr key={k.id} className="loyalty-zeile" onClick={() => navigate(kontoPfad(k.id))}>
                    <td data-label="Name">
                      <a href={kontoPfad(k.id)} className="loyalty-name" onClick={onLinkClick(kontoPfad(k.id))}>{k.name ?? k.email ?? 'ohne Namen'}</a>
                    </td>
                    <td data-label="Stufe">{stufeAnzeige(k.stufeNr, k.stufeName)}</td>
                    <td data-label="seit">{datum(k.stufeSeit)}</td>
                    <td data-label="Royal">{k.royal ? <span className="loyalty-marke">Royal</span> : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {state.daten.gesamt > state.daten.eintraege.length && (
            <p className="loyalty-fuss">{state.daten.eintraege.length} von {state.daten.gesamt} Konten – bitte grenzen Sie die Suche ein.</p>
          )}
        </div>
      )}
    </>
  );
}

function Konto({ id, verwalten }: { id: string; verwalten: boolean }) {
  const [state, setDaten] = useLaden<LoyaltyKonto>(() => loyaltyApi.konto(id), [id]);
  if (state.kind !== 'ok') return <Status state={state} />;
  const k = state.daten;
  return (
    <div className="loyalty-konto">
      <p>
        <a href="/loyalty/konten" onClick={onLinkClick('/loyalty/konten')}>← Alle Konten</a>
      </p>
      <div className="pb-card loyalty-karte">
        <h2 className="loyalty-karte__titel">{k.name ?? k.email ?? 'ohne Namen'}</h2>
        <dl className="loyalty-daten">
          <dt>Stufe</dt>
          <dd>
            {stufeAnzeige(k.stufeNr, k.stufeName)}
            {k.royal && <span className="loyalty-marke">Royal</span>}
          </dd>
          <dt>Stufe seit</dt>
          <dd>{datum(k.stufeSeit)}</dd>
          <dt>Teilnahme seit</dt>
          <dd>{datum(k.teilnahmeAt)} ({k.teilnahmeQuelle})</dd>
          <dt>Personalisierung</dt>
          <dd>{k.personalisierungAt ? `eingewilligt am ${datum(k.personalisierungAt)}` : 'keine Einwilligung'}</dd>
        </dl>
      </div>
      <div className="pb-card loyalty-tabelle-wrap">
        <table className="loyalty-tabelle">
          <caption className="loyalty-caption">Stufenhistorie</caption>
          <thead>
            <tr>
              <th scope="col">Stufe</th>
              <th scope="col">von</th>
              <th scope="col">bis</th>
              <th scope="col">Grund</th>
              <th scope="col">durch</th>
            </tr>
          </thead>
          <tbody>
            {k.stufenhistorie.map((z) => (
              <tr key={z.id}>
                <td data-label="Stufe">{stufeAnzeige(z.stufeNr, z.stufeName)}</td>
                <td data-label="von">{datum(z.gueltigVon)}</td>
                <td data-label="bis">{z.gueltigBis ? datum(z.gueltigBis) : 'offen'}</td>
                <td data-label="Grund">{z.grund}{z.kommentar ? ` – ${z.kommentar}` : ''}</td>
                <td data-label="durch">{akteurName(z.erstelltVon)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {verwalten && <RoyalAktion konto={k} onFertig={setDaten} />}
    </div>
  );
}

function RoyalAktion({ konto, onFertig }: { konto: LoyaltyKonto; onFertig: (k: LoyaltyKonto) => void }) {
  const [begruendung, setBegruendung] = useState('');
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const feldId = useId();
  const entziehen = konto.royal;
  const absenden = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (begruendung.trim().length < 3) {
        setMeldung('Bitte geben Sie eine Begründung an (mindestens 3 Zeichen).');
        return;
      }
      setBusy(true);
      setMeldung(null);
      try {
        const k = entziehen ? await loyaltyApi.royalEntziehen(konto.id, begruendung) : await loyaltyApi.royalEinladen(konto.id, begruendung);
        setBegruendung('');
        onFertig(k);
      } catch (err) {
        setMeldung(err instanceof HttpError && err.text ? err.text : 'Das hat nicht geklappt. Bitte versuchen Sie es erneut.');
      } finally {
        setBusy(false);
      }
    },
    [begruendung, entziehen, konto.id, onFertig],
  );
  const aktiv = konto.royalEinladungen.find((r) => !r.entzogenAt);
  return (
    <form className="pb-card loyalty-karte loyalty-royal" onSubmit={absenden}>
      <h2 className="loyalty-karte__titel">Royal</h2>
      <p className="loyalty-hinweis">
        {entziehen
          ? `Eingeladen am ${datum(aktiv?.eingeladenAt ?? null)} von ${aktiv ? akteurName(aktiv.eingeladenVon) : '—'}, garantiert bis ${datum(aktiv?.gueltigBis ?? null)}.`
          : 'Royal wird nur auf Einladung vergeben und ist ab der Einladung 24 Monate garantiert.'}
      </p>
      <label htmlFor={feldId} className="loyalty-suche__label">Begründung (Pflicht)</label>
      <textarea id={feldId} className="pb-input loyalty-royal__feld" value={begruendung} maxLength={1000} rows={3} onChange={(e) => setBegruendung(e.target.value)} />
      {meldung && <p className="loyalty-fehler" role="alert">{meldung}</p>}
      <div>
        <button type="submit" className={entziehen ? 'pb-button pb-button--quiet' : 'pb-button'} disabled={busy}>
          {entziehen ? 'Royal entziehen' : 'Zu Royal einladen'}
        </button>
      </div>
    </form>
  );
}

function Regelwerk() {
  const [state] = useLaden<LoyaltyRegelwerk>(() => loyaltyApi.regelwerk(), []);
  if (state.kind !== 'ok') return <Status state={state} />;
  const rw = state.daten;
  const gruppen = vorteileJeStufe(rw.vorteile);
  return (
    <div className="loyalty-regelwerk">
      <p className="loyalty-hinweis">
        Version {rw.nr}, gültig ab {datum(rw.gueltigAb)} – {rw.kommentar}. Änderungen legen immer eine neue Version an. Mit „offen“ markierte Punkte sind noch nicht entschieden.
      </p>
      <div className="pb-card loyalty-tabelle-wrap">
        <table className="loyalty-tabelle">
          <caption className="loyalty-caption">Stufen</caption>
          <thead>
            <tr>
              <th scope="col">Stufe</th>
              <th scope="col">Zugang</th>
              <th scope="col">Crowns</th>
              <th scope="col">Hinweis</th>
            </tr>
          </thead>
          <tbody>
            {rw.stufen.map((s) => (
              <tr key={s.nr}>
                <td data-label="Stufe">
                  {stufeAnzeige(s.nr, s.name)}
                  {s.offen && <span className="loyalty-offen">offen</span>}
                </td>
                <td data-label="Zugang">{schwelleAnzeige(s.schwelleEur)}</td>
                <td data-label="Crowns">{crownsAnzeige(s.crownsJeEur)}{s.keinVerfall ? ', kein Verfall' : ''}</td>
                <td data-label="Hinweis">{s.hinweis ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rw.stufen.map((s, i) => (
        <details key={s.nr} className="pb-card loyalty-stufe" open={i === 0}>
          <summary className="loyalty-stufe__kopf">
            Vorteile {stufeAnzeige(s.nr, s.name)} <span className="loyalty-stufe__zahl">({(gruppen.get(s.nr) ?? []).length})</span>
          </summary>
          <div className="loyalty-tabelle-wrap">
          <table className="loyalty-tabelle">
            <caption className="sr-only">Vorteile {stufeAnzeige(s.nr, s.name)}</caption>
            <thead>
              <tr>
                <th scope="col">Nr.</th>
                <th scope="col">Vorteil</th>
                <th scope="col">Art</th>
                <th scope="col">Häufigkeit</th>
              </tr>
            </thead>
            <tbody>
              {(gruppen.get(s.nr) ?? []).map((v) => (
                <tr key={v.code}>
                  <td data-label="Nr.">{v.code}</td>
                  <td data-label="Vorteil">
                    <span className="loyalty-name">{v.titel}</span>
                    {v.offen && <span className="loyalty-offen">offen</span>}
                    {v.regel && <span className="loyalty-regel">{v.regel}{v.ersetztCode ? ` (ersetzt ${v.ersetztCode})` : ''}</span>}
                  </td>
                  <td data-label="Art">{artAnzeige(v.art)}</td>
                  <td data-label="Häufigkeit">{haeufigkeit(v) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </details>
      ))}
      <div className="pb-card loyalty-tabelle-wrap">
        <table className="loyalty-tabelle">
          <caption className="loyalty-caption">Einstellungen (nur lesen, Version {rw.nr})</caption>
          <thead>
            <tr>
              <th scope="col">Einstellung</th>
              <th scope="col">Wert</th>
            </tr>
          </thead>
          <tbody>
            {rw.einstellungen.map((e) => (
              <tr key={e.schluessel}>
                <td data-label="Einstellung">
                  {e.beschreibung}
                  {e.offen && <span className="loyalty-offen">offen</span>}
                </td>
                <td data-label="Wert">{wertAnzeige(e)}</td>
              </tr>
            ))}
            {rw.bonusAktionen.map((b) => (
              <tr key={b.code}>
                <td data-label="Einstellung">
                  Bonus-Crowns: {b.titel}
                  {b.offen && <span className="loyalty-offen">offen</span>}
                </td>
                <td data-label="Wert">{bonusAnzeige(b)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
